import { Observable, of } from 'rxjs';
import { map, filter, switchMap, distinctUntilChanged } from 'rxjs/operators';

import { CookieBannerEvents } from '../actions';
import { CookieBannerStore } from '../types';

/**
 * Check if a cookie exists in the browser
 */
function cookieExists(name: string): boolean {
    const cookies = document.cookie.split(';');

    for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();

        if (cookie.indexOf(name + '=') === 0) {
            return true;
        }
    }

    return false;
}

/**
 * Delete a cookie if it exists and return whether it was deleted
 */
function deleteCookieIfExists(name: string, domain?: string): boolean {
    const exists = cookieExists(name);

    if (!exists) {
        return false;
    }

    // Delete the cookie by setting its expiration date to a past date
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

    /**
     * If a domain is provided, try to delete it from that domain
     * This is useful for cookies that are set with a specific domain
     * and might not be deleted by the default path.
     */
    if (domain) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`;

        if (domain.startsWith('.')) {
            const domainWithoutDot = domain.substring(1);
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domainWithoutDot};`;
        } else {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain};`;
        }

        const currentDomain = window.location.hostname;

        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${currentDomain};`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${currentDomain};`;
    }

    return cookieExists(name);
}

export function cookieCleanupEpic(_action$: Observable<any>, state$: Observable<CookieBannerStore>): Observable<any> {
    return state$.pipe(
        map((state) => ({
            categories: state.cookieBanner?.categories || {},
            consented: state.cookieBanner?.consented === true,
            cookieScan: state.cookieBanner?.cookieScan,
            hasScanData: Boolean(state.cookieBanner?.cookieScan?.cookies?.length),
            ready: state.cookieBanner?.ready === true,
        })),

        // Only proceed when relevant properties change
        distinctUntilChanged(
            (prev, curr) =>
                prev.ready === curr.ready &&
                prev.consented === curr.consented &&
                prev.hasScanData === curr.hasScanData &&
                JSON.stringify(prev.categories) === JSON.stringify(curr.categories),
        ),

        // Only proceed when all conditions are met
        filter((state) => state.ready && state.consented && state.hasScanData),

        /**
         * Loop through the cookies from the scan data and delete any cookies
         * that are not in the categories the user has consented to.
         * If a cookie is deleted, it will be added to the cookiesRemoved array.
         * This will emit an action with the categories and the cookies that were removed.
         *
         * Note: To avoid issues with Cookies not detected by CookieFirst,
         * this process will only delete cookies listed in Cookie First's
         * scanned list.
         */
        switchMap((state) => {
            const { categories, cookieScan } = state;
            const cookies = cookieScan?.cookies || [];
            const cookiesRemoved: string[] = [];

            cookies.forEach((cookie) => {
                const category = cookie.category;
                const name = cookie.name;

                if (!category || !name) {
                    return;
                }

                if (categories[category] === true) {
                    return;
                }

                const wasDeleted = deleteCookieIfExists(name, cookie.domain);

                if (wasDeleted) {
                    cookiesRemoved.push(name);
                }
            });

            return of({
                type: CookieBannerEvents.CLEANUP,
                payload: {
                    categories,
                    cookiesRemoved,
                },
            });
        }),
    );
}

export default cookieCleanupEpic;
